import { useEffect, useMemo, useState } from 'react';

import { CardPosition } from './CardPosition';
import { RankingTable } from './RankingTable';

import { ArrowDateSelector } from '@/components/GenericsTournaments/ArrowDateSelector';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTourney from '@/hooks/useTourney';
import { FCupParticipants, FCupViewTeamResponse } from '@/types/friendCup';

export const RankingSelector = () => {
  const { isMobile, isDesktop } = useIsDesktop();
  const { tourneyData } = useTourney();
  const { userInitialData: user } = useAuth();
  const [team, setTeam] = useState<FCupParticipants[] | null>();

  const tourney: FCupViewTeamResponse = useMemo(() => {
    return tourneyData?.tourney;
  }, [tourneyData?.tourney]);

  const loggedUser = useMemo(
    () =>
      tourney?.participantesGrupo?.find((item) => {
        return item.idUsuario === user?.idUsuarioDT;
      }),
    [tourney, user?.idUsuarioDT]
  );

  useEffect(() => {
    if (loggedUser && tourney?.participantesGrupo) {
      const rest = tourney?.participantesGrupo.filter((item) => {
        return item.idUsuario !== loggedUser?.idUsuario;
      });

      const sortParticipantsByAlphabetic = () => {
        const teamSorted = [loggedUser].concat(
          rest.sort((a, b) => a.apellido.localeCompare(b.apellido))
        );
        setTeam(teamSorted);
      };

      if (
        tourneyData?.matchDay &&
        tourney?.nuevoPuntosEquipo &&
        tourneyData.matchDay.orden
      ) {
        try {
          // Create a map of players by idUsuario for efficient lookups
          const playersMap = new Map(
            tourney.participantesGrupo.map((player) => [
              player.idUsuario,
              player,
            ])
          );

          // Find the match data for the current match day
          const currentMatchData = Object.values(
            tourney.nuevoPuntosEquipo
          ).find((match) => match.ordenFecha === tourneyData.matchDay?.orden);

          if (currentMatchData?.puntos?.length) {
            // Map scores to players directly using the playersMap
            const updatedTeam = currentMatchData.puntos
              .filter((score) => playersMap.has(score.idUsuario || 0)) // Ensure player exists
              .map((score) => {
                const player = playersMap.get(score.idUsuario || 0);
                if (!player) {
                  throw new Error(
                    `Player with ID ${score.idUsuario} not found`
                  );
                }
                return {
                  ...player,
                  puntos: score.puntosDT || 0, // Provide default value if puntosDT is undefined
                };
              });

            const sortedTeam: FCupParticipants[] = updatedTeam.sort((a, b) => {
              // If a is the priority user, it comes first
              if (a.idUsuario === loggedUser.idUsuario) return -1;
              // If b is the priority user, it comes first
              if (b.idUsuario === loggedUser.idUsuario) return 1;
              // Otherwise, sort alphabetically by last name
              return (a.apellido || '').localeCompare(b.apellido || '');
            });

            // Update state only if we have results
            if (sortedTeam.length > 0) {
              setTeam(sortedTeam);
            }
          } else {
            sortParticipantsByAlphabetic();
          }
        } catch (error) {
          console.error('Error processing tournament data:', error);
          // Optionally handle the error in the UI
        }
      } else {
        if (rest.length !== 0) {
          sortParticipantsByAlphabetic();
        } else {
          // team only creator
          setTeam([loggedUser]);
        }
      }
    }
  }, [
    loggedUser,
    tourney?.nuevoPuntosEquipo,
    tourney?.participantesGrupo,
    tourneyData.matchDay,
  ]);

  const data = useMemo(() => {
    if (tourney?.fechasCmbVerEquipo2) {
      return tourney?.fechasCmbVerEquipo2;
    }

    if (tourney?.fechaEqIncompleto) {
      return [tourney?.fechaEqIncompleto];
    }

    return [];
  }, [tourney?.fechaEqIncompleto, tourney?.fechasCmbVerEquipo2]);

  const showInvite = useMemo(() => {
    if (loggedUser && tourney?.fechaEqIncompleto) {
      return true;
    }
    return false;
  }, [tourney?.fechaEqIncompleto, loggedUser]);

  if (!tourney) return;

  return (
    <>
      {/* card posicion en ... */}
      {isMobile && !showInvite && <CardPosition data={tourney.puntajes} />}

      {/* selector de fechas */}
      {(tourney?.fechasCmbVerEquipo2 ||
        (tourney?.fechaEqIncompleto && isDesktop)) && (
        <ArrowDateSelector
          key={tourney.fechasCmbVerEquipo2?.filter((item) => item.id).join()} // reseteo selector de fechas si cambia tab primario o nuevo torneo
          disabled={Boolean(tourney.fechaEqIncompleto)}
          matchsDaysAvailables={data}
        />
      )}

      {/* tabla de ranking */}
      <RankingTable showInvite={showInvite} team={team || []} />
    </>
  );
};

RankingSelector.displayName = 'RankingSelector';
